import React from 'react';
import { Box, createTimePickerOptions } from '@drivekyte/web-components';
import { useMedia } from '@drivekyte/ui';
import styled from 'styled-components';
import HandoverOutpostConfirmationDialog from '@/components/handover-outpost-confirmation';
import EndDate from '@/components/search-bar/components/end-date/end-date';
import InputAddress from '@/components/search-bar/components/input-address';
import StartDate from '@/components/search-bar/components/start-date/start-date';
import useCalendarValidDates from '@/hooks/use-calendar-valid-dates';
import useSearch from '@/hooks/use-search';
import useTranslation from '@/hooks/use-translation';
import AppStoreRating from '../../../app-store-rating';
import SearchBoxContainer from '../components/search-box-container';
import SearchSubmit from '../components/search-submit';
import DeliveryAndReturnToggler from './components/delivery-and-return-toggler';
import Divider from './components/divider';

const SearchContent = () => {
  const media = useMedia();
  const isMobile = media.xs;
  const { t } = useTranslation('components.search.search-content');
  const { t: tCommon } = useTranslation('', { ns: 'common' });
  const { t: tSearch } = useTranslation('components.growth.search');

  const {
    bookingCart,
    inputState,
    isFetchingQuotes,
    isFormValid,
    isLoadingTimeSlots,
    onChange,
    onChangeAddressToggle,
    onChangeEndAddress,
    onChangeEndDate,
    onChangeStartAddress,
    onChangeStartDate,
    onSubmitWithQuotes,
    setInputState,
    timeSlotsData,
    startTimeSlotsTimestamps,
    endTimeSlotsTimestamps,
    timeSlotsStartError,
    timeSlotsEndError,
    handoverOutpostState,
    acceptHandoverSuggestion,
    rejectHandoverSuggestions,
  } = useSearch();

  useCalendarValidDates({
    startTimeSlotsTimestamps,
    endTimeSlotsTimestamps,
    startTz: timeSlotsData?.start.tz,
    endTz: timeSlotsData?.end.tz,
    selectedDates: bookingCart,
    onChange,
  });

  return (
    <Container>
      <SearchBoxContainer onSubmit={onSubmitWithQuotes}>
        {bookingCart.isSameDeliveryReturnLocations ? (
          <Box display="flex" flex={6.5} flexGrow={1}>
            <InputAddress
              active={inputState.showStartAddress}
              initialInputValue={
                bookingCart.startPlace ?? bookingCart.startAddress
              }
              label={t('start-and-end-address.input.label')}
              onSelect={onChangeStartAddress}
              placeholder={tSearch('input-placeholder')}
              testID="address-search-input"
              loadingTestID="address-search-loading"
            />
            <Divider />
          </Box>
        ) : (
          <Box display="flex" flex={6.5} flexGrow={1}>
            <Box width="50%">
              <InputAddress
                active={inputState.showStartAddress}
                initialInputValue={
                  bookingCart.startPlace ?? bookingCart.startAddress
                }
                onSelect={onChangeStartAddress}
                label={t('start-address.input.label')}
                placeholder={tSearch('input-placeholder')}
              />
            </Box>
            <Divider />
            <Box width="50%">
              <InputAddress
                active={inputState.showEndAddress}
                initialInputValue={
                  bookingCart.endPlace ?? bookingCart.endAddress
                }
                onSelect={onChangeEndAddress}
                label={t('end-address.input.label')}
                placeholder={tSearch('input-placeholder')}
              />
            </Box>
            <Divider />
          </Box>
        )}
        <Box display="flex" flex={2.5} cursor="pointer">
          <StartDate
            active={!timeSlotsStartError && inputState.showStartDate}
            label={t('start-date.input.label')}
            onChange={onChangeStartDate}
            onChangeInputState={setInputState}
            startDate={bookingCart.startDate}
            timePickerProps={{
              tooltip: isMobile ? undefined : tCommon('pick-up-spread'),
              loading: isLoadingTimeSlots,
              options:
                timeSlotsData?.start.timeSlots ||
                createTimePickerOptions(
                  7,
                  22,
                  bookingCart.startDate,
                  '2-digit',
                ),
              titleText: t('start-date.time-picker.title'),
            }}
            hasNoOptions={timeSlotsData?.start.timeSlots.length === 0}
            emptyTitleText={t('start-date.empty-state')}
            disabled={timeSlotsStartError}
          />
        </Box>
        <Divider />
        <Box display="flex" flex={2.5} cursor="pointer">
          <EndDate
            active={!timeSlotsEndError && inputState.showEndDate}
            endDate={bookingCart.endDate}
            label={t('end-date.input.label')}
            onChange={onChangeEndDate}
            onChangeInputState={setInputState}
            startDate={bookingCart.startDate}
            timePickerProps={{
              tooltip: isMobile ? undefined : tCommon('pick-up-spread'),
              loading: isLoadingTimeSlots,
              options:
                timeSlotsData?.end.timeSlots ||
                createTimePickerOptions(7, 22, bookingCart.endDate, '2-digit'),
              titleText: t('end-date.time-picker.title'),
            }}
            disabled={timeSlotsEndError}
            emptyTitleText={t('end-date.empty-state')}
            hasNoOptions={timeSlotsData?.end.timeSlots.length === 0}
          />
        </Box>
        <Box display="flex" margin="small">
          <SearchSubmit
            cta={tSearch('submit')}
            loading={isFetchingQuotes || isLoadingTimeSlots}
            disabled={!isFormValid}
          />
        </Box>
      </SearchBoxContainer>
      <Box
        alignItems="center"
        display="flex"
        justifyContent="space-between"
        flexDirection="row"
      >
        <Box alignItems="center" display="flex" flexDirection="row">
          <DeliveryAndReturnToggler
            value={bookingCart.isSameDeliveryReturnLocations}
            onChange={onChangeAddressToggle}
          />
        </Box>
        <AppStoreRating />
      </Box>
      {handoverOutpostState.isConfirmationEnabled && (
        <HandoverOutpostConfirmationDialog
          visible
          handoverOutpostState={handoverOutpostState}
          onSubmit={acceptHandoverSuggestion}
          onClose={rejectHandoverSuggestions}
        />
      )}
    </Container>
  );
};

const Container = styled.div`
  background-color: rgba(255, 255, 255, 0.9);
  padding: ${({ theme }) => theme.spacings.base}px;
  border-radius: 10px;
`;

export default SearchContent;
